import React, {useCallback, useContext, useMemo, useState} from 'react';

import {FilterInput} from 'src/components/input';
import {UserLink} from 'src/components/links';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {USER_TABS} from 'src/constants';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {DEFAULT_PAGE_SIZES} from 'src/hooks/common-data-service/cached-table';
import {
    ProductAccessTermStatus,
    SeatAssignmentStatusLabel,
} from 'src/modules/profile/user/components/product-access-term-status';
import {useAllUserSeatAssignments} from 'src/modules/profile/user/hooks/use-user-seat-assignments';
import {AssignSeatBtn} from 'src/modules/profile/user/tabs/products/assign-btn/assign-seat-btn';
import {formatLicenseDate} from 'src/modules/profile/utils/formatLicenseDate';
import {SeatAssignmentStatus} from 'src/openapi/admin/models/SeatAssignmentStatus';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {FlatButton} from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {SortHeaderWithDataTable} from '@pb/kit-component-sort-header';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';

import {RevokeSeatModal} from './revoke-seat-modal/revoke-seat-modal';
import {filterAndSortSeatAssignments, paginateSeatAssignments} from './seat-assignment-utils';

import {productHeader} from './product.module.styl';


import type {UserSeatAssignment} from 'src/openapi/admin/models/UserSeatAssignment';

type Props = {
    userId: number,
    accountId: number,
    userName: string,
};

export const ProductsTab: React.FC<Props> = ({userId, accountId, userName}) => {
    const {locale} = useContext(LocaleContext);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const {assignments, error, refresh} = useAllUserSeatAssignments(
        userId,
        SeatAssignmentStatus.ACTIVE,
    );
    const [sort, setSort] = useState<{field: 'startDate' | 'endDate', direction: 'ASC' | 'DESC'}>({
        field: 'startDate', direction: 'ASC',
    });
    const [revokedAssignment, setRevokedAssignment] = useState<UserSeatAssignment | null>(null);
    const [revokeBusy, setRevokeBusy] = useState(false);
    const filteredAssignments = useMemo(
        () => filterAndSortSeatAssignments(assignments || [], search, sort.field, sort.direction),
        [assignments, search, sort],
    );
    const currentPage = Math.min(page, Math.max(1, Math.ceil(filteredAssignments.length / pageSize)));
    const pageAssignments = useMemo(
        () => paginateSeatAssignments(filteredAssignments, currentPage, pageSize),
        [filteredAssignments, currentPage, pageSize],
    );

    const changeSearch = useCallback((value: string) => {
        setSearch(value);
        setPage(1);
    }, []);
    const changePageSize = useCallback((nextPageSize: number) => {
        setPageSize(nextPageSize);
        setPage(1);
    }, []);
    const changeSort = useCallback((field: 'startDate' | 'endDate', direction: 'ASC' | 'DESC') => {
        setSort({field, direction});
        setPage(1);
    }, []);
    const closeRevokeModal = useCallback(() => {
        if (!revokeBusy) {
            setRevokedAssignment(null);
        }
    }, [revokeBusy]);
    const {canEditUserProducts} = useAdminAccessRight();
    const canRevokeSeat = useMemo(() => canEditUserProducts(accountId), [canEditUserProducts, accountId]);

    return (
        <ServerResponseHandler serverError={error} sectionName="Assigned Seats">
            <Group direction="v" gap="m">
                <Group justify="space-between" alignItems="center" className={productHeader}>
                    <FilterInput
                        id="user-seat-assignment-search"
                        width={300}
                        value={search}
                        placeholder="Search by product name"
                        onChange={changeSearch}
                        onClear={() => changeSearch('')}
                        timeOut={300}
                    />
                    <AssignSeatBtn userId={userId} accountId={accountId} onAssigned={refresh}/>
                </Group>
                <BoxLoading loading={assignments === null}>
                    <Table fixed={true}>
                        <Head>
                            <Row>
                                <Cell as="th" scope="col" border={true}><strong>Product</strong></Cell>
                                <Cell width={120} as="th" scope="col" border={true}><strong>Term Status</strong></Cell>
                                <Cell width={120} as="th" scope="col" border={true}><strong>Assigned Date</strong></Cell>
                                <Cell as="th" scope="col" border={true}><strong>Assigned By</strong></Cell>
                                <Cell width={120} as="th" scope="col" border={true}><strong>Seat Status</strong></Cell>
                                {(['startDate', 'endDate'] as const).map(field => (
                                    <Cell width={120} key={field} as="th" scope="col" border={true}>
                                        <SortHeaderWithDataTable
                                            caption={field === 'startDate' ? 'Start Date' : 'End Date'}
                                            defaultDirection="ASC"
                                            direction={sort.field === field ? sort.direction : null}
                                            filterActive={true}
                                            onSort={direction => changeSort(field, direction as 'ASC' | 'DESC')}
                                        />
                                    </Cell>
                                ))}
                                {
                                    canRevokeSeat
                                        ? (
                                                <Cell width={80} as="th" scope="col" border={true}>
                                                    <strong>Actions</strong>
                                                </Cell>
                                            )
                                        : null
                                }
                            </Row>
                        </Head>
                        <Body>
                            {pageAssignments.length ? pageAssignments.map((assignment, index) => (
                                <Row key={assignment.id} showZebra={index % 2 === 0}>
                                    <Cell><strong>{assignment.product.name}</strong></Cell>
                                    <Cell>
                                        <Group direction="h" alignItems="center" gap="s">
                                            <ProductAccessTermStatus status={assignment.subscriptionLifecycleStatus}/>
                                        </Group>
                                    </Cell>
                                    <Cell>{formatLicenseDate(locale.dateFormat, assignment.assignedAt)}</Cell>
                                    <Cell>
                                        {(assignment.assignedBy && assignment.assignedBy.userId) ? (
                                            <UserLink
                                                place="Product Tab"
                                                userId={assignment.assignedBy.userId}
                                                caption={assignment.assignedBy.email}
                                                route={USER_TABS.ROLE_SETTINGS.name}
                                            />
                                        ) : 'Automated Process'}
                                    </Cell>
                                    <Cell>
                                        <Group direction="h" alignItems="center" gap="s">
                                            <SeatAssignmentStatusLabel status={assignment.status}/>
                                        </Group>
                                    </Cell>
                                    <Cell>
                                        {formatLicenseDate(locale.dateFormat, assignment.subscriptionStartDate)}
                                    </Cell>
                                    <Cell>
                                        {formatLicenseDate(locale.dateFormat, assignment.subscriptionEndDate)}
                                    </Cell>
                                    {
                                        canRevokeSeat
                                            ? (
                                                    <Cell>
                                                        <FlatButton onClick={() => setRevokedAssignment(assignment)}>
                                                            Revoke
                                                        </FlatButton>
                                                    </Cell>
                                                )
                                            : null
                                    }
                                </Row>
                            )) : (
                                <Row>
                                    <Cell colSpan={8} style={{textAlign: 'center'}}>
                                        <div style={{
                                            height: 450,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            Sorry, your search has no results for the specified criteria
                                        </div>
                                    </Cell>
                                </Row>
                            )}
                        </Body>
                    </Table>
                    <div style={{margin: 16}}>
                        <PaginationStripe
                            goToPage={true}
                            pageSize={pageSize}
                            totalCount={filteredAssignments.length}
                            currentPage={currentPage}
                            onPaginationChange={pagination => setPage(pagination.currentPage)}
                            onChangePaginationSize={(_currentPage, nextPageSize) => changePageSize(nextPageSize)}
                            sizeSwitcherPosition="LEFT"
                            sizeSwitcherType="ONLY_PAGE_SIZE"
                            sizes={DEFAULT_PAGE_SIZES}
                        />
                    </div>
                </BoxLoading>
                <Dialog
                    shown={Boolean(revokedAssignment)}
                    setShown={value => {
                        if (!value) {
                            closeRevokeModal();
                        }
                    }}
                    wrapperProps={{style: {width: 700}}}>
                    {revokedAssignment ? (
                        <RevokeSeatModal
                            userId={userId}
                            userName={userName}
                            assignment={revokedAssignment}
                            onBusyChange={setRevokeBusy}
                            onClose={closeRevokeModal}
                            onRevoked={refresh}
                        />
                    ) : null}
                </Dialog>
            </Group>
        </ServerResponseHandler>
    );
};
